"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Type,
  User,
  FileText,
  Plus,
  X,
  Check,
  Calendar as CalendarIcon,
  Users as WorkAreaIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import moment from "moment";
import { AnchoredPopover, getElementRef } from "@/components/tailwind/ui/anchored-popover";
import { cn } from "@/lib/utils";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { useWorkAreaContext } from "@/contexts/workAreaContext";
import { useAuth } from "@/hooks/use-auth";
import {
  recentWorkspace,
  searchWorkspace,
  type SearchDateRange,
  type SearchHit,
} from "@/services-frontend/search/searchClient";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { PropertyInputSheet } from "../board/properties/inputs/PropertyInputSheet";
import { MemberAvatar } from "@/components/tailwind/ui/memberAvatar";
import { renderIcon } from "@/components/tailwind/editor/renderIcon";

/**
 * Displayable picks. Kept as `{id, name|title}` rather than the full member /
 * hit / workArea shape so the chips don't pin the rest of those objects in
 * memory and so URL serialization is trivial.
 */
export interface CreatedByPick {
  id: string;
  name: string;
}
export interface ParentPagePick {
  id: string;
  title: string;
}
export interface WorkAreaPick {
  id: string;
  name: string;
}

/**
 * Local UI shape for the date filter. Mirrors `SearchDateRange` from the
 * client but we keep a separate `preset` discriminant so the chip can render
 * "Last 7 days" instead of recomputing the range from numeric bounds.
 */
export type DatePreset = "today" | "last7days" | "last30days" | "custom";
export interface DateFilter {
  preset: DatePreset;
  field: "updatedAt" | "createdAt"; // we currently only expose updatedAt in the UI
  after?: number;
  before?: number;
}

export interface SearchFilters {
  titleOnly: boolean;
  /** Empty/undefined means "any creator". */
  createdBy?: CreatedByPick[];
  /** Empty/undefined means "anywhere in the workspace". */
  parentPage?: ParentPagePick[];
  /** Optional filter. Undefined = no filter; presence renders the Date chip. */
  date?: DateFilter;
  /**
   * Optional filter. Undefined = no filter; presence renders the WorkArea
   * chip. The chip only surfaces WorkAreas the current user is a member of
   * (or owns / has implicit access to via an open WorkArea).
   */
  workArea?: WorkAreaPick[];
}

interface Props {
  value: SearchFilters;
  onChange: (next: SearchFilters) => void;
  workspaceId?: string;
}

/* -------------------------------------------------------------------------- */
/* MobileSheetOrPopover                                                        */
/* -------------------------------------------------------------------------- */

/**
 * On desktop: renders an AnchoredPopover anchored to the trigger.
 * On mobile: the trigger becomes a normal button and the content opens as
 * a portal'd bottom sheet via PropertyInputSheet. The same `open` /
 * `onOpenChange` semantics work in both cases, so every chip (CreatedBy /
 * InPage / Date / Teamspace) and the + Filter menu use this without
 * duplicating the conditional logic.
 */
function MobileSheetOrPopover({
  open,
  onOpenChange,
  trigger,
  title,
  desktopContentClassName,
  desktopAlign = "start",
  desktopSideOffset = 6,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactElement;
  title: string;
  desktopContentClassName: string;
  desktopAlign?: "start" | "center" | "end";
  desktopSideOffset?: number;
  children: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const setAnchor = React.useCallback((node: HTMLElement | null) => {
    if (node) setAnchorEl((prev) => (prev === node ? prev : node));
  }, []);

  if (isMobile) {
    // The wrapped trigger fires onOpenChange directly. Existing onClick
    // handlers on the trigger (e.g. the inline × clear button inside
    // ChipTrigger) call e.stopPropagation() so they don't bubble up and
    // re-open the sheet.
    const triggerWithClick = React.cloneElement(trigger as React.ReactElement<any>, {
      onClick: () => onOpenChange(!open),
    });
    return (
      <>
        {triggerWithClick}
        <PropertyInputSheet
          open={open}
          onClose={() => onOpenChange(false)}
          title={title}
          desktopAnchorClassName=""
        >
          {children}
        </PropertyInputSheet>
      </>
    );
  }

  const anchoredTrigger = React.cloneElement(trigger as React.ReactElement<any>, {
    ref: (node: HTMLElement | null) => {
      setAnchor(node);
      const childRef = getElementRef(trigger);
      if (typeof childRef === "function") childRef(node);
      else if (childRef) (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
    },
    "aria-expanded": open,
    onClick: (event: React.MouseEvent) => {
      (trigger.props as any).onClick?.(event);
      if (!event.defaultPrevented) onOpenChange(!open);
    },
  });

  return (
    <>
      {anchoredTrigger}
      {open && (
        <AnchoredPopover
          anchor={anchorEl}
          onClose={() => onOpenChange(false)}
          placement={desktopAlign === "center" ? "bottom" : `bottom-${desktopAlign}`}
          offsetPx={desktopSideOffset}
          className={desktopContentClassName}
        >
          <div
            style={{ display: "contents" }}
            onKeyDown={(e) => {
              if (e.key === "Escape" && !e.defaultPrevented) {
                e.stopPropagation();
                onOpenChange(false);
              }
            }}
          >
            {children}
          </div>
        </AnchoredPopover>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Top-level row                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Filter chip row under the search input.
 *
 *  - "Title only" is a toggle chip (click to flip).
 *  - "Created by" / "In" are always visible multi-select pickers.
 *  - "Date" and "WorkArea" are OPTIONAL filters. They render as a chip only
 *    while a value is set; otherwise they live behind the "+ Filter" menu.
 *    Dismissing a chip clears the filter and the entry pops back into the +
 *    menu — same UX as a quick-find.
 */
export function SearchFilterChips({ value, onChange, workspaceId }: Props) {
  // Track which optional filters are visible as chips. We keep this in
  // *derived* state: "has a value" → "chip visible". A chip can also be
  // forced-open temporarily (e.g. just after adding it from the + menu)
  // before the user has picked any value yet — captured in `pendingOpen`.
  const [pendingOpen, setPendingOpen] = useState<null | "date" | "workArea">(null);

  const hasDate = !!value.date;
  const hasWorkArea = !!value.workArea && value.workArea.length > 0;

  const showDateChip = hasDate || pendingOpen === "date";
  const showWorkAreaChip = hasWorkArea || pendingOpen === "workArea";

  // The + Filter menu offers exactly the filters not currently visible.
  const addable: Array<{ kind: "date" | "workArea"; label: string; icon: React.ReactNode }> = [];
  if (!showDateChip) addable.push({ kind: "date", label: "Date", icon: <CalendarIcon className="h-3.5 w-3.5" /> });
  if (!showWorkAreaChip) addable.push({ kind: "workArea", label: "WorkArea", icon: <WorkAreaIcon className="h-3.5 w-3.5" /> });

  return (
    // Wrap chips to multiple rows on mobile (no hidden horizontal-scroll
    // trap), single-row + scroll on desktop. `gap-y-1.5` keeps vertical
    // breathing room when wrapped.
    <div className="flex flex-wrap md:flex-nowrap items-center gap-1.5 md:overflow-x-auto px-3 md:px-6 py-2">
      <Chip
        active={value.titleOnly}
        onClick={() => onChange({ ...value, titleOnly: !value.titleOnly })}
        icon={<Type className="h-3.5 w-3.5" />}
        label="Title only"
      />

      <CreatedByChip value={value} onChange={onChange} />

      <InPageChip value={value} onChange={onChange} workspaceId={workspaceId} />

      {showDateChip ? (
        <DateChip
          value={value}
          onChange={onChange}
          openOnMount={pendingOpen === "date"}
          onClosed={() => setPendingOpen(null)}
        />
      ) : null}

      {showWorkAreaChip ? (
        <WorkAreaChip
          value={value}
          onChange={onChange}
          workspaceId={workspaceId}
          openOnMount={pendingOpen === "workArea"}
          onClosed={() => setPendingOpen(null)}
        />
      ) : null}

      <AddFilterButton
        addable={addable}
        onPick={(kind) => setPendingOpen(kind)}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Selection helpers                                                           */
/* -------------------------------------------------------------------------- */

function toggleById<T extends { id: string }>(list: T[] | undefined, item: T): T[] {
  const current = list ?? [];
  const idx = current.findIndex((x) => x.id === item.id);
  if (idx >= 0) return current.filter((_, i) => i !== idx);
  return [...current, item];
}

function summarizeSelection(items: { name?: string; title?: string }[]): string {
  const head = items[0];
  if (!head) return "";
  const first = head.name ?? head.title ?? "New page";
  if (items.length === 1) return first;
  return `${first} +${items.length - 1}`;
}

/* -------------------------------------------------------------------------- */
/* "+ Filter" dropdown                                                         */
/* -------------------------------------------------------------------------- */

function AddFilterButton({
  addable,
  onPick,
}: {
  addable: Array<{ kind: "date" | "workArea"; label: string; icon: React.ReactNode }>;
  onPick: (kind: "date" | "workArea") => void;
}) {
  const [open, setOpen] = useState(false);

  // When every optional filter is already active there is nothing to add —
  // we keep the button visible but disabled so the layout doesn't jump.
  const disabled = addable.length === 0;

  return (
    <MobileSheetOrPopover
      open={open}
      onOpenChange={setOpen}
      title="Add filter"
      desktopContentClassName="z-[10000] w-72 min-w-[180px] overflow-hidden rounded-xl border border-component bg-modal p-1 text-popover-foreground shadow-2xl outline-none"
      trigger={
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex shrink-0 items-center gap-1 rounded-xl px-2 py-1 text-sm text-muted-foreground",
            disabled ? "opacity-50 cursor-default" : "hover-component ",
          )}
          aria-label="Add filter"
        >
          <Plus className="h-3.5 w-3.5" />
          Filter
        </button>
      }
    >
      {addable.map((item) => (
        <button
          key={item.kind}
          type="button"
          onClick={() => {
            setOpen(false);
            // Wait for Radix to fully unmount this menu (one
            // requestAnimationFrame is enough) before signalling the
            // parent to mount the new chip. Mounting both in the same
            // frame causes the new popover's outside-pointer-down handler
            // to see the original click event as an outside interaction.
            window.requestAnimationFrame(() => onPick(item.kind));
          }}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover-component "
        >
          <span className="text-muted-foreground">{item.icon}</span>
          <span>{item.label}</span>
        </button>
      ))}
    </MobileSheetOrPopover>
  );
}

/* -------------------------------------------------------------------------- */
/* "Created by" chip                                                           */
/* -------------------------------------------------------------------------- */

function CreatedByChip({
  value,
  onChange,
}: {
  value: SearchFilters;
  onChange: (f: SearchFilters) => void;
}) {
  const { workspaceMembers, getMemberName } = useWorkspaceContext();
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState("");

  const selected = value.createdBy ?? [];
  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    const list = workspaceMembers ?? [];
    if (!q) return list.slice(0, 50);
    return list
      .filter(
        (m) =>
          m.userName?.toLowerCase().includes(q) ||
          m.userEmail?.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [workspaceMembers, filterText]);

  const isActive = selected.length > 0;
  const label = isActive ? `Created by ${summarizeSelection(selected)}` : "Created by";

  return (
    <MobileSheetOrPopover
      open={open}
      onOpenChange={setOpen}
      title="Created by"
      desktopContentClassName="z-[10000] w-[280px] overflow-hidden rounded-xl border border-component bg-modal p-4 text-popover-foreground shadow-2xl outline-none"
      trigger={
        <ChipTrigger
          icon={<User className="h-3.5 w-3.5" />}
          label={label}
          isActive={isActive}
          onClear={() => onChange({ ...value, createdBy: undefined })}
        />
      }
    >
      <div className="border-b border-component p-2">
        <input
          autoFocus
          placeholder="Filter people…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="w-full rounded-md border border-component bg-background px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="max-h-[280px] overflow-y-auto p-1">
        {filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            No members match
          </div>
        ) : (
          filtered.map((m) => {
            const id = String(m.userId);
            const name = m.userName || m.userEmail || "Someone";
            const isSelected = selectedIds.has(id);
            return (
              <button title="Toggle filter"
                key={id}
                type="button"
                onClick={() =>
                  onChange({
                    ...value,
                    createdBy: toggleById(selected, { id, name }),
                  })
                }
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover-component ",
                  isSelected && "bg-selected ",
                )}
                aria-pressed={isSelected}
              >
                <MemberAvatar
                  name={m.userName}
                  email={m.userEmail}
                  userId={m.userId}
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] font-medium uppercase text-gray-600 dark:text-gray-300"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{getMemberName(m.userId) || getMemberName(m.userEmail) || m.userName || "Unnamed"}</div>
                  {m.userEmail ? (
                    <div className="truncate text-[10px] text-muted-foreground">{m.userEmail}</div>
                  ) : null}
                </div>
                <Check
                  className={cn(
                    "h-3.5 w-3.5 shrink-0 text-blue-500 transition-opacity",
                    isSelected ? "opacity-100" : "opacity-0",
                  )}
                />
              </button>
            );
          })
        )}
      </div>
      <SelectionFooter
        count={selected.length}
        onClear={() => onChange({ ...value, createdBy: undefined })}
      />
    </MobileSheetOrPopover>
  );
}

/* -------------------------------------------------------------------------- */
/* "In <page>" chip                                                            */
/* -------------------------------------------------------------------------- */

function InPageChip({
  value,
  onChange,
  workspaceId,
}: {
  value: SearchFilters;
  onChange: (f: SearchFilters) => void;
  workspaceId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const selected = value.parentPage ?? [];
  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  // Fetch recent / search pages whenever the popover is open.
  useEffect(() => {
    if (!open || !workspaceId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const run = async () => {
      const q = query.trim();
      try {
        const resp = q
          ? await searchWorkspace({
              workspaceId,
              query: q,
              types: ["page", "collection_view"],
              limit: 20,
              signal: ctrl.signal,
            })
          : await recentWorkspace({
              workspaceId,
              types: ["page", "collection_view"],
              limit: 20,
              signal: ctrl.signal,
            });
        if (abortRef.current === ctrl) setHits(resp.hits);
      } catch {
        if (abortRef.current === ctrl) setHits([]);
      }
    };

    const t = window.setTimeout(run, 100);
    return () => {
      window.clearTimeout(t);
      ctrl.abort();
    };
  }, [open, query, workspaceId]);

  // Always show currently-selected pages at the top — even if the active
  // search query would otherwise filter them out — so the user can deselect.
  const visibleEntries = useMemo(() => {
    const seen = new Set<string>();
    const out: { id: string; title: string; icon?: string; breadcrumb?: { id: string; title: string }[] }[] = [];
    for (const s of selected) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      const hit = hits.find((h) => h.id === s.id);
      out.push({
        id: s.id,
        title: hit ? stripHtml(hit.title) || s.title : s.title,
        icon: hit?.icon,
        breadcrumb: hit?.breadcrumb,
      });
    }
    for (const h of hits) {
      if (seen.has(h.id)) continue;
      seen.add(h.id);
      out.push({
        id: h.id,
        title: stripHtml(h.title) || "New page",
        icon: h.icon,
        breadcrumb: h.breadcrumb,
      });
    }
    return out;
  }, [hits, selected]);

  const isActive = selected.length > 0;
  const label = isActive ? `In ${summarizeSelection(selected)}` : "In";

  return (
    <MobileSheetOrPopover
      open={open}
      onOpenChange={setOpen}
      title="In page"
      desktopContentClassName="z-[10000] w-[320px] overflow-hidden rounded-xl border border-component bg-modal p-4 text-popover-foreground shadow-2xl outline-none"
      trigger={
        <ChipTrigger
          icon={<FileText className="h-3.5 w-3.5" />}
          label={label}
          isActive={isActive}
          onClear={() => onChange({ ...value, parentPage: undefined })}
        />
      }
    >
      <div className="border-b border-component p-2">
        <input
          autoFocus
          placeholder="Search pages…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-md border border-component bg-background px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="max-h-[280px] overflow-y-auto p-1">
        {visibleEntries.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            {query ? "No pages match" : "No recent pages"}
          </div>
        ) : (
          visibleEntries.map((entry) => {
            const isSelected = selectedIds.has(entry.id);
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() =>
                  onChange({
                    ...value,
                    parentPage: toggleById(selected, {
                      id: entry.id,
                      title: entry.title || "New page",
                    }),
                  })
                }
                className={cn(
                  "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm hover-component ",
                  isSelected && "bg-selected ",
                )}
                aria-pressed={isSelected}
              >
                <div className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground">
                  {entry.icon ? (
                    <span className="text-sm leading-none">{renderIcon(entry.icon, { style: { width: 16, height: 16 } })}</span>
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{entry.title || "New page"}</div>
                  {entry.breadcrumb && entry.breadcrumb.length > 0 ? (
                    <div className="truncate text-[10px] text-muted-foreground">
                      {entry.breadcrumb.map((c) => c.title).join(" / ")}
                    </div>
                  ) : null}
                </div>
                <Check
                  className={cn(
                    "mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500 transition-opacity",
                    isSelected ? "opacity-100" : "opacity-0",
                  )}
                />
              </button>
            );
          })
        )}
      </div>
      <SelectionFooter
        count={selected.length}
        onClear={() => onChange({ ...value, parentPage: undefined })}
      />
    </MobileSheetOrPopover>
  );
}

/* -------------------------------------------------------------------------- */
/* "Date" chip                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Resolves a preset → numeric range. We compute relative-to-now at filter
 * time (and again at render-of-label time) so a chip created at 11:59pm
 * doesn't keep ranging over "yesterday" past midnight.
 */
function resolvePreset(preset: DatePreset, now = Date.now()): { after?: number; before?: number } {
  const m = moment(now);
  if (preset === "today") {
    return { after: m.clone().startOf("day").valueOf(), before: m.clone().endOf("day").valueOf() };
  }
  if (preset === "last7days") {
    return { after: m.clone().subtract(7, "days").startOf("day").valueOf(), before: m.clone().endOf("day").valueOf() };
  }
  if (preset === "last30days") {
    return { after: m.clone().subtract(30, "days").startOf("day").valueOf(), before: m.clone().endOf("day").valueOf() };
  }
  return {};
}

function formatDateRangeLabel(d: DateFilter): string {
  if (d.preset === "today") return "Today";
  if (d.preset === "last7days") return "Last 7 days";
  if (d.preset === "last30days") return "Last 30 days";
  if (d.after && d.before) {
    const a = moment(d.after);
    const b = moment(d.before);
    if (a.isSame(b, "day")) return a.format("MMM D, YYYY");
    if (a.isSame(b, "year")) return `${a.format("MMM D")} – ${b.format("MMM D, YYYY")}`;
    return `${a.format("MMM D, YYYY")} – ${b.format("MMM D, YYYY")}`;
  }
  if (d.after) return `After ${moment(d.after).format("MMM D, YYYY")}`;
  if (d.before) return `Before ${moment(d.before).format("MMM D, YYYY")}`;
  return "Date";
}

function DateChip({
  value,
  onChange,
  openOnMount,
  onClosed,
}: {
  value: SearchFilters;
  onChange: (f: SearchFilters) => void;
  openOnMount: boolean;
  onClosed: () => void;
}) {
  // Start CLOSED even when openOnMount is true. We then schedule the open on
  // the next animation frame — this gives the click event that opened us
  // (from the + menu) time to fully resolve. If we opened synchronously,
  // Radix's `onPointerDownOutside` would see that original click and
  // immediately close the popover ("flashes for a frame, then gone").
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!openOnMount) return;
    const id = window.requestAnimationFrame(() => setOpen(true));
    return () => window.cancelAnimationFrame(id);
    // Only react to the initial value of openOnMount — the parent clears it
    // back to false the moment we report `onClosed`, and we don't want to
    // re-open then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const date = value.date;
  const isActive = !!date;
  const label = date ? formatDateRangeLabel(date) : "Date";

  const handleClear = () => {
    onChange({ ...value, date: undefined });
    // After dismiss, give control back to the parent so the Date entry can
    // reappear in the + menu.
    onClosed();
  };

  return (
    <MobileSheetOrPopover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // If the user closes without picking, AND the chip is currently
        // empty (we were freshly added from the + menu), tell the parent so
        // the chip disappears and the + menu re-includes us.
        if (!next && !date) onClosed();
      }}
      title="Date"
      desktopContentClassName="z-[10000] w-[300px] overflow-hidden rounded-xl border border-component bg-modal p-4 text-popover-foreground shadow-2xl outline-none"
      trigger={
        <ChipTrigger
          icon={<CalendarIcon className="h-3.5 w-3.5" />}
          label={label}
          isActive={isActive}
          onClear={handleClear}
        />
      }
    >
      <DatePickerBody
        current={date}
        onPick={(next) => {
          onChange({ ...value, date: next });
          setOpen(false);
        }}
        onClear={handleClear}
      />
    </MobileSheetOrPopover>
  );
}

function DatePickerBody({
  current,
  onPick,
  onClear,
}: {
  current?: DateFilter;
  onPick: (next: DateFilter) => void;
  onClear: () => void;
}) {
  // Calendar focus month — used only for navigation. Falls back to "now"
  // when there's no current selection.
  const [focus, setFocus] = useState(() => moment(current?.after ?? Date.now()).startOf("month"));

  // Pending custom-range selection (first click sets `start`; second click
  // sets `end`; clicking again restarts). We don't propagate until the user
  // picks both bounds, so the chip doesn't half-update mid-drag.
  const [pendingStart, setPendingStart] = useState<number | undefined>(current?.preset === "custom" ? current.after : undefined);
  const [pendingEnd, setPendingEnd] = useState<number | undefined>(current?.preset === "custom" ? current.before : undefined);

  const pickPreset = (preset: Exclude<DatePreset, "custom">) => {
    const range = resolvePreset(preset);
    onPick({ preset, field: "updatedAt", ...range });
  };

  const onDayClick = (day: moment.Moment) => {
    const ts = day.clone().startOf("day").valueOf();
    if (pendingStart === undefined || (pendingStart !== undefined && pendingEnd !== undefined)) {
      // Fresh selection
      setPendingStart(ts);
      setPendingEnd(undefined);
      return;
    }
    // Second click — finalize. Swap if user clicked earlier than start.
    const start = Math.min(pendingStart, ts);
    const end = Math.max(pendingStart, ts);
    setPendingStart(start);
    setPendingEnd(end);
    onPick({
      preset: "custom",
      field: "updatedAt",
      after: moment(start).startOf("day").valueOf(),
      before: moment(end).endOf("day").valueOf(),
    });
  };

  return (
    <div className="flex flex-col">
      {/* Preset row */}
      <div className="flex flex-col p-1">
        <PresetButton
          label="Today"
          active={current?.preset === "today"}
          onClick={() => pickPreset("today")}
        />
        <PresetButton
          label="Last 7 days"
          active={current?.preset === "last7days"}
          onClick={() => pickPreset("last7days")}
        />
        <PresetButton
          label="Last 30 days"
          active={current?.preset === "last30days"}
          onClick={() => pickPreset("last30days")}
        />
      </div>

      <div className="h-px bg-gray-200 dark:bg-[rgb(42,42,42)]" />

      {/* Month nav */}
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-xs font-medium">{focus.format("MMMM YYYY")}</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setFocus((f) => f.clone().subtract(1, "month"))}
            className="rounded p-1 hover-component "
            aria-label="Previous month"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setFocus((f) => f.clone().add(1, "month"))}
            className="rounded p-1 hover-component "
            aria-label="Next month"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Calendar grid */}
      <CalendarGrid
        focus={focus}
        rangeStart={pendingStart}
        rangeEnd={pendingEnd}
        onDayClick={onDayClick}
      />

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-component px-2 py-1.5 text-[10px]">
        <span className="text-muted-foreground">
          {current ? formatDateRangeLabel(current) : "Pick a date or range"}
        </span>
        {current ? (
          <button
            type="button"
            onClick={onClear}
            className="rounded px-1.5 py-0.5 text-muted-foreground hover-component "
          >
            Clear
          </button>
        ) : null}
      </div>
    </div>
  );
}

function PresetButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs hover-component ",
        active && "bg-gray-100 dark:bg-[rgb(42,42,42)]",
      )}
      aria-pressed={active}
    >
      <span>{label}</span>
      <Check
        className={cn(
          "h-3.5 w-3.5 text-blue-500 transition-opacity",
          active ? "opacity-100" : "opacity-0",
        )}
      />
    </button>
  );
}

/**
 * 7-column day grid. Highlights:
 *   - the start/end of the range with a solid blue circle
 *   - inclusive interior days with a muted blue background
 *   - "today" with a red ring (only when not selected)
 *
 * Renders 6 weeks (42 cells) so the grid height is stable across months.
 * Days from the previous/next month are rendered greyed-out and still
 * clickable — clicking one jumps the focus to that month.
 */
function CalendarGrid({
  focus,
  rangeStart,
  rangeEnd,
  onDayClick,
}: {
  focus: moment.Moment;
  rangeStart?: number;
  rangeEnd?: number;
  onDayClick: (m: moment.Moment) => void;
}) {
  const start = focus.clone().startOf("month").startOf("week");
  const today = moment().startOf("day").valueOf();

  const days: moment.Moment[] = [];
  for (let i = 0; i < 42; i++) days.push(start.clone().add(i, "days"));

  const inMonth = (d: moment.Moment) => d.month() === focus.month();
  const isStart = (d: moment.Moment) => rangeStart !== undefined && d.startOf("day").valueOf() === moment(rangeStart).startOf("day").valueOf();
  const isEnd = (d: moment.Moment) => rangeEnd !== undefined && d.startOf("day").valueOf() === moment(rangeEnd).startOf("day").valueOf();
  const isInRange = (d: moment.Moment) => {
    if (rangeStart === undefined || rangeEnd === undefined) return false;
    const v = d.startOf("day").valueOf();
    return v > rangeStart && v < rangeEnd;
  };
  const isToday = (d: moment.Moment) => d.startOf("day").valueOf() === today;

  return (
    <div className="px-2 pb-2">
      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-muted-foreground">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((label) => (
          <div key={label} className="py-1">{label}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((d) => {
          const inThisMonth = inMonth(d);
          const start = isStart(d);
          const end = isEnd(d);
          const middle = isInRange(d);
          const today = isToday(d);

          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => onDayClick(d)}
              className={cn(
                "relative h-7 w-7 rounded-full text-[11px] transition-colors",
                !inThisMonth && "text-muted-foreground/50",
                inThisMonth && !start && !end && !middle && "hover-component ",
                middle && "bg-blue-500/20 text-foreground",
                (start || end) && "bg-blue-500 text-white font-medium",
                today && !start && !end && "ring-1 ring-red-500/70",
              )}
              aria-label={d.format("LL")}
            >
              {d.date()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* "WorkArea" chip                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Decides whether to surface this workArea in the user's picker.
 *
 * Open workAreas grant access to every workspace member implicitly — without
 * the user's id appearing anywhere in `members[]`. So a strict "is in
 * members[]" check would hide them, which is wrong.
 *
 * We invert the rule: SHOW by default and only HIDE a private workArea the
 * user isn't a member/owner of. Concretely:
 *   - `accessLevel === 'private'` AND (not owner) AND (not in members[])
 *       → hidden (the user has no direct path to this workArea)
 *   - everything else → shown
 *
 * Why default-show:
 *   1. Open workAreas: every workspace member can reach them, so they must
 *      appear regardless of explicit membership.
 *   2. WorkAreas missing `accessLevel` (legacy records, partial syncs) are
 *      visible — being too eager to hide them risks an empty picker.
 *   3. Group access can't be checked from the client (we don't have the
 *      user's group list here). Defaulting to show means a user with
 *      group-only access still sees their workArea in the picker; if they
 *      shouldn't, the server's permission filter still scopes results.
 *
 * The eventual access enforcement is the server's permission filter on the
 * search request itself — so this picker filter is purely UX (don't list
 * workAreas the user definitely cannot use). It's NOT a security boundary.
 */
function userCanSeeWorkArea(workArea: unknown, userId: string): boolean {
  if (!workArea || typeof workArea !== "object") return false;
  const wa = workArea as {
    ownerId?: unknown;
    accessLevel?: string;
    members?: Array<{ userId?: unknown }>;
  };
  // Default visible. Only private workAreas get the strict check.
  if (wa.accessLevel !== "private") return true;
  if (!userId) return true; // auth still loading — don't pre-hide
  if (String(wa.ownerId ?? "") === userId) return true;
  if (Array.isArray(wa.members)) {
    for (const m of wa.members) {
      if (String(m?.userId ?? "") === userId) return true;
    }
  }
  return false;
}

function WorkAreaChip({
  value,
  onChange,
  workspaceId,
  openOnMount,
  onClosed,
}: {
  value: SearchFilters;
  onChange: (f: SearchFilters) => void;
  workspaceId?: string;
  openOnMount: boolean;
  onClosed: () => void;
}) {
  // Same deferred-open trick as DateChip — see comment there. Without this,
  // the click from the + menu race-closes the freshly-opened picker.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!openOnMount) return;
    const id = window.requestAnimationFrame(() => setOpen(true));
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reads from the same context the sidebar uses (API-loaded, in React
  // state) — NOT from IndexedDB. The IDB mirror isn't reliably populated
  // for every account, so reading from it left the picker empty for users
  // whose workAreas hadn't been synced into IDB. The workspace context's
  // `currentWorkspace` already gates the data to the right workspace.
  const { workAreas } = useWorkAreaContext();
  const { user } = useAuth();
  const userId = String(user?.id ?? "");

  const [filterText, setFilterText] = useState("");

  const selected = value.workArea ?? [];
  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  // Restrict the picker to workAreas the user can reach. `userCanSeeWorkArea`
  // is intentionally permissive — it only hides PRIVATE workAreas the user
  // isn't a member/owner of. Open workAreas (which grant access to every
  // workspace member implicitly) and records with no/unknown accessLevel
  // remain visible. The picker is UX-only; the actual access boundary is
  // the server-side permission filter on the search request.
  const myWorkAreas = useMemo(
    () => (workAreas ?? []).filter((w) => userCanSeeWorkArea(w, userId)),
    [workAreas, userId],
  );

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return myWorkAreas.slice(0, 50);
    return myWorkAreas
      .filter((w) => (w.name ?? "").toLowerCase().includes(q))
      .slice(0, 50);
  }, [myWorkAreas, filterText]);

  const isActive = selected.length > 0;
  const label = isActive ? `WorkArea ${summarizeSelection(selected)}` : "WorkArea";

  const handleClear = () => {
    onChange({ ...value, workArea: undefined });
    onClosed();
  };

  return (
    <MobileSheetOrPopover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Close-without-selection → tell parent so the chip disappears.
        if (!next && selected.length === 0) onClosed();
      }}
      title="Workarea"
      desktopContentClassName="z-[10000] w-[280px] overflow-hidden rounded-xl border border-component bg-modal p-4 text-popover-foreground shadow-2xl outline-none"
      trigger={
        <ChipTrigger
          icon={<WorkAreaIcon className="h-3.5 w-3.5" />}
          label={label}
          isActive={isActive}
          onClear={handleClear}
        />
      }
    >
<div className="border-b border-component p-2">
            <input
              autoFocus
              placeholder="Filter workAreas…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full rounded-md border border-component bg-background px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-[280px] overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                {filterText
                  ? "No workAreas match"
                  : myWorkAreas.length === 0
                    ? "No workAreas available"
                    : "No workAreas"}
              </div>
            ) : (
              filtered.map((w) => {
                const id = String((w as any)._id ?? (w as any).id ?? "");
                const name = (w as any).name ?? "Untitled";
                const isSelected = selectedIds.has(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() =>
                      onChange({
                        ...value,
                        workArea: toggleById(selected, { id, name }),
                      })
                    }
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover-component ",
                      isSelected && "bg-selected ",
                    )}
                    aria-pressed={isSelected}
                  >
                    <WorkAreaIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1 truncate">{name}</div>
                    <Check
                      className={cn(
                        "h-3.5 w-3.5 shrink-0 text-blue-500 transition-opacity",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </button>
                );
              })
            )}
          </div>
          <SelectionFooter
            count={selected.length}
            onClear={() => onChange({ ...value, workArea: undefined })}
          />
    </MobileSheetOrPopover>
  );
}

/* -------------------------------------------------------------------------- */
/* Reusable bits                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Trigger button shared by every multi-select chip. Renders the icon + label
 * + (when active) an inline × that clears the filter without opening the
 * popover. Forwards refs to make Radix's `asChild` happy.
 */
const ChipTrigger = React.forwardRef<
  HTMLButtonElement,
  {
    icon: React.ReactNode;
    label: string;
    isActive: boolean;
    onClear: () => void;
  }
>(function ChipTrigger({ icon, label, isActive, onClear, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-sm transition-colors",
        isActive
          ? "bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30"
          : "border-component bg-transparent text-muted-foreground hover-component ",
      )}
      aria-label={label}
    >
      {icon}
      {/* Tighter truncation on mobile so chips fit two-per-row inside the
          bottom sheet instead of overflowing off-screen. */}
      <span className="max-w-[110px] md:max-w-[180px] truncate">{label}</span>
      {isActive ? (
        <span
          role="button"
          tabIndex={0}
          aria-label={`Clear ${label}`}
          className="ml-0.5 rounded-full p-0.5 hover-component "
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onClear();
            }
          }}
        >
          <X className="h-3 w-3" />
        </span>
      ) : null}
    </button>
  );
});

function SelectionFooter({ count, onClear }: { count: number; onClear: () => void }) {
  if (count === 0) return null;
  return (
    <div className="flex items-center justify-between border-t border-component px-2 py-1.5 text-[10px]">
      <span className="text-muted-foreground">{count} selected</span>
      <button
        type="button"
        onClick={onClear}
        className="rounded px-1.5 py-0.5 text-muted-foreground hover-component "
      >
        Clear
      </button>
    </div>
  );
}

/**
 * The server can return `title` with highlight `<em>` tags embedded. For
 * chip / picker labels we want the plain text.
 */
function stripHtml(s: string): string {
  return s.replace(/<[^>]+>/g, "");
}

/* -------------------------------------------------------------------------- */
/* Simple toggle chip (used only by "Title only")                              */
/* -------------------------------------------------------------------------- */

interface ChipProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

function Chip({ icon, label, active, disabled, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-sm transition-colors",
        active
          ? "bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30"
          : "border-component bg-transparent text-muted-foreground hover-component ",
        disabled ? "cursor-default opacity-70" : "cursor-pointer",
      )}
    >
      {icon}
      {/* Tighter truncation on mobile (matches ChipTrigger). */}
      <span className="max-w-[110px] md:max-w-[180px] truncate">{label}</span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Public helper for SearchModal — converts the chip-state Date filter into   */
/* the wire-shape SearchDateRange. Exported so the modal doesn't need to     */
/* know about presets at all; it just passes whatever the user picked.       */
/* -------------------------------------------------------------------------- */

export function dateFilterToWire(d: DateFilter | undefined): SearchDateRange | undefined {
  if (!d) return undefined;
  // For presets we recompute "now" so a chip created at 11:59pm doesn't keep
  // ranging over "yesterday" past midnight.
  if (d.preset === "today" || d.preset === "last7days" || d.preset === "last30days") {
    const range = resolvePreset(d.preset);
    if (range.after === undefined && range.before === undefined) return undefined;
    return { field: d.field, ...range };
  }
  if (d.after === undefined && d.before === undefined) return undefined;
  return { field: d.field, after: d.after, before: d.before };
}

// Retained for future use when a filter value has a check-mark list.
export const _CheckIcon = Check;
